import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Label } from '@nucleus-commerce/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useWorkflowTemplateSummaries } from '@/features/workflows';

import { useSwitchWorkflow } from '../hooks/use-workflow-mutations';

import { ActionDialog } from './action-dialog';

export interface SwitchWorkflowDialogProps {
  storeId: string;
  orderId: string;
  orderName: string;
  /** The template the order runs now; null when it has no workflow yet. */
  currentTemplateId: string | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Moves the order to another template; also attaches the first workflow to a paid order. The
 * names come from the summary listing, which `orders.write` may read.
 */
export function SwitchWorkflowDialog({
  storeId,
  orderId,
  orderName,
  currentTemplateId,
  onOpenChange,
}: SwitchWorkflowDialogProps) {
  const { t } = useTranslation(['production', 'errors']);
  const message = useErrorMessage();
  const templates = useWorkflowTemplateSummaries(storeId);
  const switchTemplate = useSwitchWorkflow(storeId, orderId);
  const items = templates.data ?? [];
  const others = items.filter((tpl) => tpl.id !== currentTemplateId);
  const fallback = others.find((tpl) => tpl.isDefault) ?? others[0];
  const [chosen, setChosen] = useState<string | null>(null);
  const templateId = chosen ?? fallback?.id ?? '';
  const noOther = templates.isSuccess && others.length === 0;
  const error = switchTemplate.error;
  const fieldError = isApiError(error)
    ? error.details.find((d) => d.field === 'body.templateId')?.message
    : undefined;
  const rootError = error && !fieldError ? message(error) : undefined;

  return (
    <ActionDialog
      open
      onOpenChange={onOpenChange}
      title={t('switch.title', { order: orderName })}
      description={t('switch.description')}
      rootError={templates.isError ? t('switch.unavailable') : rootError}
      isPending={switchTemplate.isPending}
      submitDisabled={templateId === ''}
      submitLabel={t('switch.submit')}
      submittingLabel={t('switch.submitting')}
      onSubmit={(event) => {
        event.preventDefault();
        if (!templateId) return;
        switchTemplate.mutate(templateId, { onSuccess: () => onOpenChange(false) });
      }}
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="switch-template">{t('switch.template')}</Label>
        <Select
          value={templateId}
          onValueChange={setChosen}
          disabled={templates.isError || noOther}
        >
          <SelectTrigger
            id="switch-template"
            className="w-full"
            aria-invalid={fieldError ? true : undefined}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {items.map((template) => (
              <SelectItem
                key={template.id}
                value={template.id}
                disabled={template.id === currentTemplateId}
              >
                {template.id === currentTemplateId
                  ? t('switch.current', { name: template.name })
                  : template.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {noOther ? (
          <p className="text-muted-foreground text-sm" role="status">
            {currentTemplateId ? t('switch.noOther') : t('switch.noTemplates')}
          </p>
        ) : null}
        {fieldError ? <p className="text-destructive text-sm">{fieldError}</p> : null}
      </div>
    </ActionDialog>
  );
}
